/**
 * Delegated-authority FieldDefinition catalog hook for override rules.
 * Sibling to `useConditionFieldRegistry` (assignments) and
 * `useRuleSetConditionFieldRegistry` (rules_engine framework) — same shape,
 * different URL.
 *
 * Delegated-authority override rules need their own namespaced endpoint (not
 * the framework `/rules_engine/condition_fields`) because the BE applies a
 * delegated-authority-owned post-filter on top of the framework result:
 * `exposure.*` paths are dropped when the org runs in claim-level payment-limits
 * mode (DR §5.2). That filter depends on the org's `payment_limits_level`
 * config, which the framework endpoint doesn't see.
 *
 * Fetches from
 * `GET /sysconfig/api/v1/organizations/{org_id}/delegated_authority/override_rules/condition_fields`
 * with the LOB and sub-org scope the author picked in the upsert dialog, so the
 * rule is offered the same catalog it will be saved against. Each all-flag is
 * the scope its list cannot express — "every LOB / sub-org the organization has,
 * including future ones" — and the BE expands it, so a snapshot of today's LOBs
 * sent as `lobs` would mean something different as soon as the org adds one.
 *
 * Unlike the framework sibling this hook is not prerequisite-gated. The dialog
 * seeds both pickers to "All", so the first fetch already carries a scope, and
 * while the LOB picker is empty (it clears itself whenever the sub-org
 * selection changes) the request goes out as `is_all_lobs=false` with no
 * `lobs[]`, which the BE answers with the all-LOB catalog rather than a 400.
 */

import type { FieldDefinitionDto } from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder/types';
import { useConditionFieldsFetcher } from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder/useConditionFieldsFetcher';

export interface UseDelegatedAuthorityConditionFieldRegistryParams {
  organizationId: number;
  /** Caller-owned prerequisite gate. Defaults to true since the BE answers every
   *  scope the dialog can send, an emptied LOB picker included; provided so the
   *  parent can suppress the fetch in tests or while the dialog is closed. */
  enabled?: boolean;
  /** Every LOB the rule is scoped to. The catalog is the intersection across all
   *  of them, so passing a subset offers fields the saved rule can never match
   *  on. Empty when `isAllLobs`. */
  lobs: string[];
  isAllLobs: boolean;
  isAllSubOrgs: boolean;
  subOrgIds: number[];
}

export interface UseDelegatedAuthorityConditionFieldRegistryResult {
  fields: FieldDefinitionDto[];
  isLoading: boolean;
  isError: boolean;
}

export const useDelegatedAuthorityConditionFieldRegistry = ({
  organizationId,
  enabled = true,
  lobs,
  isAllLobs,
  isAllSubOrgs,
  subOrgIds,
}: UseDelegatedAuthorityConditionFieldRegistryParams): UseDelegatedAuthorityConditionFieldRegistryResult =>
  useConditionFieldsFetcher({
    url: `/sysconfig/api/v1/organizations/${organizationId}/delegated_authority/override_rules/condition_fields`,
    enabled,
    // The fetcher encodes arrays as repeated `key[]=` entries, which is exactly
    // the `lobs[]` / `sub_org_ids[]` aliases the BE params declare.
    queryParams: {
      lobs,
      is_all_lobs: isAllLobs,
      is_all_sub_orgs: isAllSubOrgs,
      sub_org_ids: subOrgIds,
    },
  });
